import React, { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Home, Calendar, ShoppingBag, MessageSquare, PawPrint, Heart, Stethoscope, Users, Building2, Sparkles, LogOut, Gift, LayoutDashboard, Package, BedDouble, ShieldCheck, MapPin, Pill, FileText, Image as ImageIcon, Crown, HandHeart, Activity, Clock, Video, Scissors, Footprints, Truck, Wallet, BadgePercent, TrendingUp, ListChecks, Shield, Menu, MoreHorizontal } from "lucide-react";
import { useAuth } from "../App";
import BrandLogo from "../components/BrandLogo";
import SubscriptionBanner from "../components/SubscriptionBanner";
import NotificationBell from "../components/NotificationBell";
import MobileNavDrawer from "../components/MobileNavDrawer";

const PARENT_TABS = [
  { to: "/app", label: "Home", icon: Home, end: true, testid: "nav-home" },
  { to: "/app/pets", label: "My Pets", icon: PawPrint, testid: "nav-pets" },
  { to: "/app/store", label: "Store", icon: ShoppingBag, testid: "nav-store" },
  { to: "/app/feed", label: "Activity", icon: MessageSquare, testid: "nav-feed" },
  { to: "/app/tracking", label: "Track", icon: MapPin, testid: "nav-tracking" },
];

const PARENT_SIDE = [
  { to: "/app/vets", label: "Vets", icon: Stethoscope },
  { to: "/app/services", label: "Services", icon: Scissors },
  { to: "/app/adoption", label: "Adoption", icon: Heart },
  { to: "/app/foster", label: "Foster", icon: Heart },
  { to: "/app/rescue-cases", label: "Rescues", icon: HandHeart },
  { to: "/app/ngos", label: "NGOs / Shelters", icon: Heart },
  { to: "/app/lost-pets", label: "Lost & Found", icon: MapPin },
  { to: "/app/meetups", label: "Meetups", icon: Users },
  { to: "/app/hostels", label: "Hostels", icon: Building2 },
  { to: "/app/pet-id", label: "Pet ID", icon: ShieldCheck },
  { to: "/app/pet-registration", label: "Pet Registration", icon: FileText },
  { to: "/app/symptom-checker", label: "AI Symptom Check", icon: Sparkles },
  { to: "/app/refer", label: "Refer & Earn", icon: Gift },
  { to: "/app/appointments", label: "Visits", icon: Calendar },
  { to: "/app/my-orders", label: "My orders", icon: Package },
  { to: "/app/billing", label: "Plans & Billing", icon: Crown },
];

const ADMIN_TABS = [
  { to: "/app/admin", label: "Admin", icon: LayoutDashboard, end: true, testid: "nav-admin" },
  { to: "/app/admin/payments", label: "Payments", icon: BadgePercent, testid: "nav-admin-payments" },
  { to: "/app/admin/financials", label: "Financials", icon: TrendingUp, testid: "nav-admin-financials" },
  { to: "/app/admin/plans", label: "Plans", icon: ListChecks, testid: "nav-admin-plans" },
  { to: "/app/admin/pet-registrations", label: "Pet Reg", icon: FileText, testid: "nav-admin-petreg" },
  { to: "/app/admin/videos", label: "Videos", icon: Video, testid: "nav-admin-videos" },
  { to: "/app", label: "Parent App", icon: Home, end: true, testid: "nav-admin-parent" },
  { to: "/app/feed", label: "Activity", icon: MessageSquare, testid: "nav-admin-feed" },
  { to: "/app/ngos", label: "NGOs", icon: Heart, testid: "nav-admin-ngos" },
  { to: "/app/rescue-cases", label: "Rescues", icon: HandHeart, testid: "nav-admin-rescues" },
  { to: "/app/lost-pets", label: "Lost", icon: MapPin, testid: "nav-admin-lost" },
];

const NGO_TABS = [
  { to: "/app/ngo/impact", label: "Impact", icon: Activity, end: true, testid: "nav-ngo-impact" },
  { to: "/app/ngo/rescue-cases", label: "Rescues", icon: HandHeart, testid: "nav-ngo-rescues" },
  { to: "/app/adoption", label: "Adoption", icon: Heart, testid: "nav-ngo-adoption" },
  { to: "/app/ngo/donations", label: "Donations", icon: Gift, testid: "nav-ngo-donations" },
  { to: "/app/ngo/register", label: "Register", icon: ShieldCheck, testid: "nav-ngo-register" },
  { to: "/app/ngo/foster-applications", label: "Fosters", icon: Home, testid: "nav-ngo-foster-apps" },
  { to: "/app/foster", label: "Listings", icon: Heart, testid: "nav-ngo-foster" },
  { to: "/app/lost-pets", label: "Lost", icon: MapPin, testid: "nav-ngo-lost" },
  { to: "/app/van-schedule", label: "Vet Van", icon: Truck, testid: "nav-ngo-van" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-ngo-earnings" },
  { to: "/app/profile", label: "Profile", icon: Users, testid: "nav-ngo-profile" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-ngo-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-ngo-settings" },
];

const STORE_TABS = [
  { to: "/app/store-dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-store-dashboard" },
  { to: "/app/store/orders", label: "Orders", icon: Package, testid: "nav-store-orders" },
  { to: "/app/store/products", label: "Products", icon: Package, testid: "nav-store-products" },
  { to: "/app/store", label: "Storefront", icon: ShoppingBag, testid: "nav-store-front" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-store-earnings" },
  { to: "/app/profile", label: "Profile", icon: Users, testid: "nav-store-profile" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-store-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-store-settings" },
];

const HOSTEL_TABS = [
  { to: "/app/hostel-dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-hostel-dashboard" },
  { to: "/app/hostel/bookings", label: "Bookings", icon: Calendar, testid: "nav-hostel-bookings" },
  { to: "/app/hostel/rooms", label: "Rooms", icon: BedDouble, testid: "nav-hostel-rooms" },
  { to: "/app/hostels", label: "Listings", icon: Building2, testid: "nav-hostel-front" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-hostel-earnings" },
  { to: "/app/profile", label: "Profile", icon: Users, testid: "nav-hostel-profile" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-hostel-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-hostel-settings" },
];

const VET_TABS = [
  { to: "/app/vet-dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-vet-dashboard" },
  { to: "/app/vet/visits", label: "Visits", icon: Calendar, testid: "nav-vet-visits" },
  { to: "/app/vet/patients", label: "Patients", icon: PawPrint, testid: "nav-vet-patients" },
  { to: "/app/vet/pro-bono", label: "Pro-bono", icon: HandHeart, testid: "nav-vet-probono" },
  { to: "/app/vet/availability", label: "Slots", icon: Clock, testid: "nav-vet-availability" },
  { to: "/app/vet/inventory", label: "Inventory", icon: Package, testid: "nav-vet-inventory" },
  { to: "/app/vet/medicines", label: "Medicines", icon: Pill, testid: "nav-vet-medicines" },
  { to: "/app/vet/template", label: "Template", icon: FileText, testid: "nav-vet-template" },
  { to: "/app/van-schedule", label: "Vet Van", icon: Truck, testid: "nav-vet-van" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-vet-earnings" },
  { to: "/app/profile", label: "Profile", icon: Users, testid: "nav-vet-profile" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-vet-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-vet-settings" },
];

const GROOMER_TABS = [
  { to: "/app/services-dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-groomer-dashboard" },
  { to: "/app/services", label: "Browse", icon: Scissors, testid: "nav-groomer-browse" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-groomer-earnings" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-groomer-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-groomer-settings" },
];

const WALKER_TABS = [
  { to: "/app/services-dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-walker-dashboard" },
  { to: "/app/services", label: "Browse", icon: Footprints, testid: "nav-walker-browse" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, testid: "nav-walker-earnings" },
  { to: "/app/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-walker-kyc" },
  { to: "/app/partner", label: "Settings", icon: Users, testid: "nav-walker-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const role = user?.role || "parent";
  const [drawerOpen, setDrawerOpen] = useState(false);

  let tabs = PARENT_TABS;
  let sideLinks = PARENT_SIDE;
  if (role === "vet") { tabs = VET_TABS; sideLinks = []; }
  else if (role === "store_owner") { tabs = STORE_TABS; sideLinks = []; }
  else if (role === "hostel_owner") { tabs = HOSTEL_TABS; sideLinks = []; }
  else if (role === "ngo") { tabs = NGO_TABS; sideLinks = []; }
  else if (role === "groomer") { tabs = GROOMER_TABS; sideLinks = []; }
  else if (role === "walker") { tabs = WALKER_TABS; sideLinks = []; }
  else if (role === "super_admin") { tabs = ADMIN_TABS; sideLinks = []; }

  // Bottom-nav layout: 4 primary tabs + a "More" button that opens the full drawer.
  // Ensures vendors (who have 8–13 tabs) can reach every screen from mobile.
  const primaryTabs = tabs.slice(0, 4);
  const hasMore = tabs.length > 4 || sideLinks.length > 0;

  return (
    <div className="min-h-screen bg-bg flex flex-col" data-testid="dashboard-layout">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-xl border-b border-line">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2" data-testid="top-brand">
            <BrandLogo variant="full" size="sm" to="/app" />
            {role !== "parent" && (
              <span className="hidden sm:inline ml-1 text-[10px] font-bold uppercase tracking-wider bg-ink text-white rounded-full px-2 py-0.5">
                {role === "vet" ? "Vet" : role === "store_owner" ? "Store" : role === "hostel_owner" ? "Hostel" : role === "ngo" ? "NGO / Shelter" : role === "groomer" ? "Groomer" : role === "walker" ? "Walker" : role === "super_admin" ? "Admin" : role}
              </span>
            )}
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {tabs.map(t => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`${t.testid}-desktop`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                    isActive ? "bg-ink text-white" : "text-ink-soft hover:text-ink hover:bg-surface-muted"
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
            {sideLinks.map(s => (
              <NavLink
                key={s.to}
                to={s.to}
                data-testid={`nav-${s.label.toLowerCase().replace(/[^a-z]+/g,'-')}-desktop`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-bold transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-soft hover:text-ink hover:bg-surface-muted"
                  }`
                }
              >
                {s.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <NotificationBell />
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-line" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-leaf grid place-items-center font-bold text-ink">
                {user?.name?.[0] ?? "P"}
              </div>
            )}
            <Link to="/app/privacy-data" data-testid="privacy-data-link" className="hidden md:inline-flex p-2 rounded-full hover:bg-surface-muted" title="Privacy & Data">
              <Shield className="w-4 h-4 text-ink-soft" />
            </Link>
            <button onClick={logout} data-testid="logout-button" className="hidden md:inline-flex p-2 rounded-full hover:bg-surface-muted" title="Sign out">
              <LogOut className="w-4 h-4 text-ink-soft" />
            </button>
            {/* Mobile hamburger — opens full drawer */}
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-button"
              className="md:hidden w-11 h-11 rounded-full grid place-items-center hover:bg-surface-muted active:bg-surface-muted"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        tabs={tabs}
        sideLinks={sideLinks}
        role={role}
        user={user}
        onLogout={logout}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-8 py-6 pb-28 md:pb-10">
        <div className="mb-4"><SubscriptionBanner /></div>
        <Outlet />
      </main>

      <nav data-testid="bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 bg-surface/95 backdrop-blur-xl border-t border-line z-50 pb-safe px-2">
        <div className="flex justify-between items-center max-w-md mx-auto">
          {primaryTabs.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 rounded-2xl transition-colors min-h-[56px] ${
                  isActive ? "text-brand" : "text-ink-soft"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`w-9 h-9 rounded-xl grid place-items-center ${isActive ? "bg-brand/10" : ""}`}>
                    <t.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold tracking-wide">{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
          {hasMore && (
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="bottom-nav-more"
              className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 rounded-2xl text-ink-soft hover:text-ink active:text-ink transition-colors min-h-[56px]"
            >
              <div className="w-9 h-9 rounded-xl grid place-items-center">
                <MoreHorizontal className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold tracking-wide">More</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}
